import type {
  EntryWithLinkData,
  IProductEnrichment
} from '~~/lib/ContentfulService';
import type { Ref } from 'vue';
import { useProductDetailPageStore } from '~/stores/productDetailPageStore/productDetailPageStore';
import { applyContentfulAssetCdnByLocale } from '~/utils/contentful/rewriteContentfulAssetHostsByLocale';

const PRODUCT_ENRICHMENT_SSR_TIMEOUT_MS = 500;

interface ProductEnrichmentAsyncData {
  rows: EntryWithLinkData<IProductEnrichment>[];
  retryOnClient: boolean;
  /**
   * `false` marks rows that were never requested, e.g. because the product was
   * still loading. Only a requested result may replace rendered enrichments.
   */
  requested: boolean;
}

interface CarriedEnrichments {
  rows: EntryWithLinkData<IProductEnrichment>[];
  locale: string;
  urlKeys: string[];
}

/**
 * A variant switch navigates to another url key, so the PDP mounts a new
 * composable instance with an empty async data entry. Without this hand-over
 * every enrichment would vanish until the new request settles. Only written and
 * read in the browser, so no state leaks between SSR requests.
 */
const carriedEnrichments = shallowRef<CarriedEnrichments | null>(null);

function createHash(rules: object): string {
  return Object.entries(rules)
    .filter((entry): entry is [string, string[]] => Array.isArray(entry[1]))
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([key, values]) => `${key}:${[...values].sort().join(',')}`)
    .join(';');
}

function createStringRecordHash(values: Record<string, string>): string {
  return Object.entries(values)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([key, value]) => `${key}:${value}`)
    .join(';');
}

async function waitForSettledStatus(
  status: Readonly<Ref<string>>
): Promise<void> {
  if (status.value === 'success' || status.value === 'error') {
    return;
  }

  await new Promise<void>((resolve) => {
    const stop = watch(status, (value) => {
      if (value === 'success' || value === 'error') {
        stop();
        resolve();
      }
    });
  });
}

export function useProductEnrichments(): ComputedRef<IProductEnrichment[]> {
  const isServer = Boolean(import.meta.server ?? import.meta.env.SSR);
  const isClient = Boolean(import.meta.client ?? !isServer);
  const route = useRoute();
  const { $activeLocale } = useNuxtApp();
  const locale = computed(() => $activeLocale.value.contentfulCode);
  const pdpStore = useProductDetailPageStore();
  const logger = useLogger();
  const usedAttributeCodesFailed = ref(false);
  const {
    public: { isPreviewEnabled }
  } = useRuntimeConfig();

  const urlKey = computed(() => String(route.params.urlKey));
  const asyncDataUrlKey = urlKey.value;
  let isActiveRoute = true;
  onBeforeRouteLeave(() => {
    isActiveRoute = false;
  });
  onBeforeRouteUpdate((to) => {
    if (String(to.params.urlKey) !== asyncDataUrlKey) {
      isActiveRoute = false;
    }
  });

  const canLoadProductEnrichments = computed(
    () => !pdpStore.loading && Boolean(pdpStore.decoratedProduct?.sku)
  );

  function collectVariantUrlKeys(): string[] {
    const siblingUrlKeys =
      pdpStore.parentProduct?.variants
        ?.map((variant) => variant?.product?.url_key)
        .filter((variantUrlKey): variantUrlKey is string =>
          Boolean(variantUrlKey)
        ) ?? [];

    return [asyncDataUrlKey, ...siblingUrlKeys];
  }

  // Depends on the locale only, so it must not sit behind the product load:
  // fetching it inside the enrichment handler delayed every enrichment by one
  // extra round trip.
  const {
    data: usedAttributeCodes,
    status: usedAttributeCodesStatus,
    refresh: refreshUsedAttributeCodes
  } = useAsyncData(
    `product-enrichment-attribute-codes-${locale.value}`,
    async () => {
      try {
        const codes = await $fetch<string[]>(
          '/api/contentful/productEnrichmentAttributeCodes',
          {
            timeout: isServer ? PRODUCT_ENRICHMENT_SSR_TIMEOUT_MS : undefined,
            query: { locale: locale.value }
          }
        );
        usedAttributeCodesFailed.value = false;
        return codes;
      } catch (error) {
        usedAttributeCodesFailed.value = true;
        logger.error('failed to load product enrichment attributes', error);
        return [];
      }
    },
    {
      default: (): string[] => [],
      watch: [locale]
    }
  );
  const usedAttributeCodesHash = computed(() =>
    [...usedAttributeCodes.value].sort().join(',')
  );
  const productEnrichmentCustomAttributes = computed(
    () =>
      pdpStore.decoratedProduct?.createProductEnrichmentCustomAttributes(
        usedAttributeCodes.value
      ) ?? {}
  );
  const enrichmentInputsHash = computed(
    () =>
      `${locale.value};${canLoadProductEnrichments.value};${createHash(
        pdpStore.productEnrichmentRules
      )};${createStringRecordHash(productEnrichmentCustomAttributes.value)}`
  );

  const result = useAsyncData(
    `product-enrichment-${asyncDataUrlKey}`,
    async () => {
      if (!isActiveRoute || urlKey.value !== asyncDataUrlKey) {
        return { rows: [], retryOnClient: false, requested: false };
      }

      const pendingProductInit = pdpStore.initPromise;
      if (pendingProductInit) {
        await pendingProductInit.catch(() => undefined);
      } else if (isServer) {
        return { rows: [], retryOnClient: true, requested: false };
      }

      await waitForSettledStatus(usedAttributeCodesStatus);

      if (usedAttributeCodesFailed.value) {
        return { rows: [], retryOnClient: isServer, requested: false };
      }

      if (!canLoadProductEnrichments.value) {
        return { rows: [], retryOnClient: isServer, requested: false };
      }

      try {
        const rows = await $fetch<EntryWithLinkData<IProductEnrichment>[]>(
          '/api/contentful/productEnrichment',
          {
            timeout: isServer ? PRODUCT_ENRICHMENT_SSR_TIMEOUT_MS : undefined,
            query: {
              locale: locale.value,
              rules: pdpStore.productEnrichmentRules,
              // Only the attribute codes targeted by enrichments are sent; passing
              // the full Magento attribute set blew up the request URL (HTTP 431).
              customAttributes: productEnrichmentCustomAttributes.value
            }
          }
        );
        return {
          rows: applyContentfulAssetCdnByLocale(rows, locale.value),
          retryOnClient: false,
          requested: true
        };
      } catch (error) {
        logger.error('failed to load product enrichments', error);
        return { rows: [], retryOnClient: isServer, requested: false };
      }
    },
    {
      dedupe: 'defer',
      lazy: true,
      default: (): ProductEnrichmentAsyncData => ({
        rows: [],
        retryOnClient: false,
        requested: false
      })
    }
  );

  watch([enrichmentInputsHash, usedAttributeCodesHash], () => {
    if (!isActiveRoute || pdpStore.initPromise) {
      return;
    }

    if (result.status.value === 'success' || result.status.value === 'error') {
      void result.refresh();
    }
  });

  if (isClient) {
    watchEffect(() => {
      const rows = result.data.value.rows;
      if (rows.length > 0) {
        carriedEnrichments.value = {
          rows,
          locale: locale.value,
          urlKeys: collectVariantUrlKeys()
        };
      }
    });

    onNuxtReady(async () => {
      if (result.data.value.retryOnClient) {
        const previousCodesHash = usedAttributeCodesHash.value;
        await refreshUsedAttributeCodes();
        if (usedAttributeCodesHash.value === previousCodesHash) {
          await result.refresh();
        }
      }
    });
  }

  function resolveDisplayRows(): EntryWithLinkData<IProductEnrichment>[] {
    const { rows, requested } = result.data.value;
    if (rows.length > 0 || requested || !isClient) {
      return rows;
    }

    const carried = carriedEnrichments.value;
    return carried?.locale === locale.value &&
      carried.urlKeys.includes(asyncDataUrlKey)
      ? carried.rows
      : rows;
  }

  return computed(() => {
    return resolveDisplayRows().map((productEnrichment) =>
      // Strip unresolved links and locale error stubs like the content page
      // path does; otherwise stubs reach the renderer and can throw (DBP-4306).
      resolveEntry(productEnrichment, true, {
        warnRemovedReferencesInBrowser: Boolean(isPreviewEnabled)
      })
    );
  });
}
